import { debounce } from 'lodash';
import { z } from 'zod';

import type { BillingInfo } from '@ctct/domain-layer';
import type { BillingInfoFormPageStore } from '@ctct/state-client-layer';

import type { UIService } from '../../_shared/Form/services/UIService';
import type { ValidationService } from '../../_shared/Form/services/ValidationService';
import type { billingInfoFormEventBus } from '../BillingInfoFormEventBus';
import type { BillingInfoFormValues, TaxInfo } from '../types';

export class BillingInfoFormService {
  constructor(
    private pageStore: BillingInfoFormPageStore,
    private billingInfo: BillingInfo,
    private uiService: UIService<BillingInfoFormValues>,
    private validation: ValidationService,
    private eventBus: typeof billingInfoFormEventBus
  ) {}

  public api() {
    return {
      validate: (value: BillingInfoFormValues) => this.getZodSchema().safeParse(value).success,
      getEmptyFormValuesArray: (): (keyof BillingInfoFormValues)[] => {
        if (this.pageStore.getState().billingInfoFormIsDisplayingCard) {
          return [];
        }

        if (!this.uiService.getFormValuesFn) throw new Error('get form values function is not added');

        const formValues = this.uiService.getFormValuesFn();

        return Object.keys(formValues)
          .filter((key) => formValues[key] === '' || formValues[key] === null)
          .map((key) => key as keyof BillingInfoFormValues);
      },

      setFormValuesIfCurrentlyEmpty: ({
        newValues,
        forceUpdateFields = [],
      }: {
        newValues: Partial<BillingInfoFormValues>;
        forceUpdateFields?: (keyof BillingInfoFormValues)[];
      }) => {
        if (this.pageStore.getState().billingInfoFormIsDisplayingCard) {
          return false;
        }

        const setValueFn = this.uiService.setFormValueFn;

        if (!setValueFn) throw new Error('set form values function is not added');

        const emptyFieldsArray = this.api().getEmptyFormValuesArray();

        Object.keys(newValues).forEach((key) => {
          if ([...emptyFieldsArray, ...forceUpdateFields].includes(key as keyof BillingInfoFormValues)) {
            setValueFn(key as keyof BillingInfoFormValues, newValues[key]);
          }
        });

        return true;
      },

      updateMissingFields: (missingFields: (keyof BillingInfoFormValues)[]) => {
        this.eventBus.emit('missingFields', missingFields, { source: 'api' });
      },
    };
  }

  public data() {
    return {
      getEmailList: () =>
        this.billingInfo.getAllEmails().map((email) => ({
          key: email,
          text: email,
          value: email,
        })),

      getZodSchema: () => this.getZodSchema(),
    };
  }

  public handlers() {
    return {
      onTaxInfoUpdate: debounce((taxInfo: TaxInfo) => {
        if (this.isValidZipcode(taxInfo.zipcode, taxInfo.country)) {
          this.eventBus.emit('taxInfoUpdated', taxInfo, { source: 'user' });
        }
      }, 1000),
    };
  }

  public validate = (value: BillingInfoFormValues) => this.getZodSchema().safeParse(value).success;

  private isValidZipcode(zipcode: string | undefined, countryCode: string | undefined): boolean {
    if (!zipcode || !countryCode) {
      return false;
    }

    const schema = z
      .object({
        zipcode: this.validation.getZipCodeValidationSchema(),
        country: z.string(),
      })
      .superRefine(({ zipcode: zip, country }, ctx) => {
        this.validation.validateZipCode(zip, country, ctx);
      });

    return schema.safeParse({ zipcode, country: countryCode }).success;
  }

  private getZodSchema() {
    const schema = z
      .object({
        firstname: this.validation.getFirstNameValidationSchema(),
        lastname: this.validation.getLastNameValidationSchema(),
        companyname: this.validation.getCompanyNameValidationSchema(),
        address1: this.validation.getAddress1ValidationSchema(),
        address2: this.validation.getAddress2ValidationSchema(),
        city: this.validation.getCityValidationSchema(),
        zipcode: this.validation.getZipCodeValidationSchema(),
        state: this.validation.getStateValidationSchema(),
        country: this.validation.getCountryValidationSchema(),
        phone: this.validation.getPhoneValidationSchema(),
        email: this.validation.getEmailValidationSchema(),
        currentTaxAgreement: this.validation.getCurrentTaxAgreementSchema(),
        vatNumber: this.validation.getVatValidationSchema(),
      })
      .superRefine(({ country, state, zipcode, currentTaxAgreement = null, vatNumber }, ctx) => {
        this.validation.validateZipCode(zipcode, country, ctx);
        this.validation.validateVatNumbers(country, currentTaxAgreement, vatNumber, ctx);
        this.validation.validateState(country, state, ctx);
      });

    return schema;
  }
}
